/**
 * アプリ設定の永続化サービス。
 *
 * I/O の実体は `./storage.ts` のラッパに集約：
 *   - Web / LIFF → `window.localStorage`
 *   - Capacitor ネイティブ → 起動時に Preferences → localStorage へ hydrate し、
 *     以降は localStorage を同期で読み書き、書き込みは Preferences にも非同期ミラー。
 *
 * これにより本ファイルの公開 API（`loadSettings()` 等）は同期のまま維持できる。
 * ネイティブで hydrate が完了する前に UI が `loadSettings()` を呼ぶと
 * デフォルト値が返る可能性があるため、アプリ起動時の早い段階で
 * `initSettingsStorage()` を await すること（`main.tsx` で実施する想定）。
 *
 * 参考: `.claude/rules/no-silent-fallback.md`（未知のエラーは伝播させる）
 *       `.claude/rules/no-hardcode.md`
 */

import {
  DEFAULT_SETTINGS,
  SETTINGS_STORAGE_KEY,
  SOUND_OPTIN_STORAGE_KEY,
  SOUND_OPTIN_VERSION,
  SUPPORTED_LOCALES,
  type AppLocale,
  type AppSettings,
} from '../config/settings'
import { hydrateStorage, readStringSync, writeString } from './storage'
import { isCapacitorNative } from '../utils/platform'

// ---------------------------------------------------------------------------
// Validation helpers (localStorage の JSON は信頼できない前提)
// ---------------------------------------------------------------------------

function parseLocale(value: unknown): AppLocale | null {
  if (typeof value !== 'string') return null
  return SUPPORTED_LOCALES.includes(value as AppLocale)
    ? (value as AppLocale)
    : null
}

/**
 * 旧スキーマ（numeric `bgmVolume` / `seVolume`）→ 新スキーマ（boolean `bgmMuted` /
 * `seMuted`）への移行を含むパース処理。
 *
 * 旧形式の判定: プロパティに `bgmVolume` or `seVolume` が number で存在する場合。
 *   - 値 0 → ミュート ON（`*Muted = true`）
 *   - それ以外 → ミュート OFF（`*Muted = false`）
 *
 * マイグレーションは読み込み時に一度完結し、次回 `saveSettings` の書き込みで
 * ストレージも新スキーマに更新される（呼び出し元で明示的な save は不要。
 * 設定画面を開いた時点で上書き保存される想定）。
 */
function coerceSettings(raw: unknown): AppSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SETTINGS }
  const obj = raw as Record<string, unknown>

  const bgmMuted = coerceMutedFlag(obj.bgmMuted, obj.bgmVolume, DEFAULT_SETTINGS.bgmMuted)
  const seMuted = coerceMutedFlag(obj.seMuted, obj.seVolume, DEFAULT_SETTINGS.seMuted)

  return {
    bgmMuted,
    seMuted,
    locale: parseLocale(obj.locale) ?? DEFAULT_SETTINGS.locale,
  }
}

/**
 * ミュートフラグのパース。新スキーマ（boolean）を最優先、旧スキーマ（numeric 音量）で
 * フォールバック、どちらも無ければデフォルト値。
 */
function coerceMutedFlag(
  newValue: unknown,
  legacyVolume: unknown,
  fallback: boolean,
): boolean {
  if (typeof newValue === 'boolean') return newValue
  // 旧形式: 0 はミュート ON、その他の有限数は OFF。NaN / 文字列等は無視してデフォルト。
  if (typeof legacyVolume === 'number' && Number.isFinite(legacyVolume)) {
    return legacyVolume === 0
  }
  return fallback
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * ネイティブ環境で Preferences → localStorage のミラーを完了させる。
 *
 * Web では no-op。呼ばないとネイティブ初回起動時に一時的にデフォルト値が
 * 表示される可能性があるため、アプリ起動直後（`main.tsx` 等）で await すること。
 */
export async function initSettingsStorage(): Promise<void> {
  // 移行フラグも hydrate 対象に含める。ネイティブではゲート自体を適用しないため
  // 現状このキーは参照されないが、`saveSettings()` が書くキーなので
  // Preferences と localStorage のミラーを揃えておく。
  await hydrateStorage([SETTINGS_STORAGE_KEY, SOUND_OPTIN_STORAGE_KEY])
  applySoundOptInMigration()
}

/**
 * 「音 OFF 既定化」が、このバージョンの版で確定済みかどうか。
 */
function isSoundOptInCurrent(): boolean {
  return readStringSync(SOUND_OPTIN_STORAGE_KEY) === SOUND_OPTIN_VERSION
}

/**
 * 保存値に関わらず無音に倒すべき状態か。
 *
 * **ブラウザ（LINE ミニアプリ / Web）限定の挙動。** ブラウザは音量調整の摩擦で
 * 離脱する事故が報告されたため OFF 既定にするが、ネイティブアプリはユーザーが
 * 「ゲームアプリ」と認識して起動するため、従来どおり音 ON 既定・保存値優先に戻す。
 */
function shouldForceSoundOff(): boolean {
  return !isCapacitorNative() && !isSoundOptInCurrent()
}

/**
 * 「音 OFF 既定化」の一回限り移行（ストレージの正規化）。ブラウザのみ実行する。
 *
 * 実際の「一度 OFF に戻す」保証は `loadSettings()` の read 側ゲートが担う。
 * ここはそれを永続化して以後のゲートを不要にするだけなので、書き込みが失敗しても
 * 振る舞いは壊れない（次回起動でも read 側で無音になる）。
 *
 * 呼び出し順の前提: `hydrateStorage()` 完了後、かつ `initVolumeFromSettings()` や
 * React 描画より前（`main.tsx`）。
 */
function applySoundOptInMigration(): void {
  if (!shouldForceSoundOff()) return
  // loadSettings() が既にミュート済みの値を返すため、そのまま保存すれば
  // 「locale 等は保存値どおり / 音だけ OFF」で正規化される
  // （saveSettings が opt-in フラグも立てる）。
  saveSettings(loadSettings())
}

/**
 * 保存された設定を読み込む（同期）。
 *
 * - 未保存 or JSON パース失敗 → デフォルト値で復帰（`// fallback:ok`）。
 *   ユーザーに影響しない「初回/破損時の既定値復帰」であり、サイレントに
 *   エラーを握り潰しているわけではない（警告ログは残す）。
 * - ネイティブ環境で hydrate 前に呼ばれた場合もデフォルト値。
 * - 旧スキーマ（numeric bgmVolume/seVolume）は自動的に boolean bgmMuted/seMuted に変換される。
 * - **ブラウザで「音 OFF 既定化」が未確定の間は、保存値に関わらず両ミュートを返す**
 *   （下記ゲート。ネイティブアプリには適用しない）。
 */
export function loadSettings(): AppSettings {
  const settings = readPersistedSettings()
  if (!shouldForceSoundOff()) return settings

  // 移行の書き込みが失敗する環境（quota 超過 / 一部 iOS Safari のプライベート
  // ブラウジング）でも旧保存値の `bgmMuted: false` が復活しないよう、read 側で
  // 無音に倒す。locale 等の音以外の設定は保存値をそのまま使う。
  return { ...settings, bgmMuted: true, seMuted: true }
}

function readPersistedSettings(): AppSettings {
  const raw = readStringSync(SETTINGS_STORAGE_KEY)
  if (raw == null) return { ...DEFAULT_SETTINGS }

  try {
    const parsed = JSON.parse(raw)
    return coerceSettings(parsed)
  } catch (err) {
    // fallback:ok — JSON 破損時は既定値で復帰（ユーザーに再設定を促すより実害が少ない）
    console.warn('[Settings] JSON parse failed, falling back to defaults:', err)
    return { ...DEFAULT_SETTINGS }
  }
}

/**
 * 設定を永続化する（localStorage 同期書き込み + ネイティブでは Preferences にミラー）。
 *
 * 書き込み失敗はログを残し、エラーを呼び出し元に伝播させる
 * （UI 側で「保存に失敗しました」等の通知ができるよう）。
 */
export function saveSettings(next: AppSettings): void {
  const json = JSON.stringify(next)
  writeString(SETTINGS_STORAGE_KEY, json)
  // 設定を保存した時点で、このバージョンの音設定は確定したものとして扱う。
  // これが無いと `loadSettings()` のゲートが効き続け、ユーザーがタイトルの
  // トグルや設定モーダルで明示 ON にしても次回起動で OFF に戻ってしまう。
  writeString(SOUND_OPTIN_STORAGE_KEY, SOUND_OPTIN_VERSION)
}

/**
 * 既存の設定をロードし、指定した部分プロパティで上書きして保存する。
 * 戻り値は保存後の完全な AppSettings。
 */
export function updateSettings(partial: Partial<AppSettings>): AppSettings {
  const current = loadSettings()
  const next: AppSettings = {
    ...current,
    ...partial,
  }
  saveSettings(next)
  return next
}
